import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip, Alert } from "@mui/material";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, DataTable } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { market, canEdit, date, num, PlayerLink, MarketLink, ConfirmAction } from "./community";

// Market → Player (service `market`, Docs/Market.md §2 / §3 / §10): one account's market - `market offers <player>` (its offers in
// every state with their pending fills, the holding "Reserved in market", the operator's `marketBlock`) and `market trades <player>`
// (its last 100 trades, both sides, every branch) - and the operator's verbs: `market cancel <offerId>`, `market block <player>
// [reason]` (its open offers cancelled and given back at once; every market action answers 60 from then on), `market unblock
// <player>` (the title's clean-account rule still applies). `#market` views, `market` acts. /<game>/market/player/<link|id>.
const STATE_COLOR = { open: "success", taking: "info", closing: "warning" };

export default class MarketPlayerTab extends React.Component {
    state = { player: "", view: null, trades: null, errors: {}, busy: false, action: null, notice: null };

    componentDidMount() {
        const pending = globals.route?.deep?.[0];
        if (pending) this.setState({ player: pending }, this.lookup);
    }

    lookup = () => {
        const p = this.state.player.trim();
        if (!p) return;
        this.setState({ view: null, trades: null, errors: {} });
        // each section on its own: a refusal of one never hides the other
        const section = (key, parts, description) => market(description, parts, { silent: true })
            .then(r => { this.setState({ [key]: r && typeof r === "object" ? r : null }); if (key === "view" && r && typeof r === "object") replaceDeep([p]); })
            .catch(e => this.setState(s => ({ errors: { ...s.errors, [key]: e?.message ?? "failed" } })));
        section("view", ["offers", p], "Loading the offers");
        section("trades", ["trades", p], "Loading the trades");
    }

    act = (parts, description, success) => {
        this.setState({ busy: true });
        return market(description, parts, { successMessage: success })
            .then(r => {
                this.setState({ action: null, notice: r?.done ? { severity: "success", text: r.done } : null });
                if (r?.view && typeof r.view === "object") this.setState({ view: r.view }); else this.lookup();
            })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    cancel = (o) => this.setState({ action: {
        title: `Cancel offer ${o.id}?`, confirmText: "Cancel the offer",
        text: `${o.kind} ${num(o.count)} × ${o.goods} at ${num(o.unitPrice)}: closed now and given back to the account.`,
        run: () => this.act(["cancel", o.id], "Cancelling the offer", "Offer cancelled"),
    }});

    block = () => {
        const v = this.state.view;
        this.setState({ action: {
            title: `Block ${v.name || v.account} from the market?`, confirmText: "Block",
            input: { label: "Reason (optional; the operator log keeps it)", initial: "", optional: true },
            text: "Every open offer of the account is cancelled and given back at once; every market action (Browse and History too) answers 60 \"not available for this account\" until unblocked.",
            run: (reason) => this.act(["block", v.account, ...(reason ? [reason] : [])], "Blocking", "Blocked"),
        }});
    }

    unblock = () => {
        const v = this.state.view;
        this.setState({ action: {
            title: `Unblock ${v.name || v.account}?`, confirmText: "Unblock",
            text: "Lifts the operator's block only: the title's clean-account rule (ban, migration verdict, a flagged match) still applies.",
            run: () => this.act(["unblock", v.account], "Unblocking", "Unblocked"),
        }});
    }

    render() {
        const { player, view, trades, errors, busy, action, notice } = this.state;
        const edit = canEdit("market");
        const err = (key) => errors[key] ? <Alert severity="warning" sx={{ mt: 1 }}>{errors[key]}</Alert> : null;
        const offerColumns = [
            { id: "state", label: "State", width: "9%", render: o => <Chip size="small" color={STATE_COLOR[o.state] ?? "default"} label={o.state}
                                                                     title={`${o.ioc ? "a Take (never opens) · " : ""}${o.escrowed ? "reserved" : "not reserved yet"}${o.returning ? ` · returning ${o.returning}` : ""} · last change ${date(o.at)}`} /> },
            { id: "kind", label: "Side", width: "6%" },
            { id: "goods", label: "Goods", width: "10%", render: o => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{o.goods}</span> },
            { id: "count", label: "Count", width: "7%", align: "right", render: o => num(o.count) },
            { id: "posted", label: "Posted", width: "7%", align: "right", render: o => num(o.posted) },
            { id: "unitPrice", label: "Unit price", width: "9%", align: "right", render: o => num(o.unitPrice) },
            { id: "branch", label: "Branch", width: "11%", render: o => <span style={{ wordBreak: "break-all" }}>{o.titleBranch}</span> },
            { id: "created", label: "Created", width: "13%", render: o => date(o.created) },
            { id: "expires", label: "Expires", width: "13%", render: o => date(o.expires) },
            { id: "pending", label: "Fills", width: "6%", align: "right", render: o => (o.pending ?? []).length
                ? <span title={(o.pending ?? []).map(f => `${f.trade}: ${f.count} at ${f.price} (fee ${f.fee}) taker ${f.taker}${f.takerDone ? " ✓taker" : ""}${f.makerDone ? " ✓maker" : ""}`).join("\n")}>{o.pending.length} pending</span> : "—" },
            { id: "actions", label: "", width: "9%", align: "right", render: o => edit && o.state === "open" ? <Button size="small" color="error" onClick={() => this.cancel(o)} disabled={busy}>Cancel</Button> : "" },
        ];
        const tradeColumns = [
            { id: "at", label: "At", width: "15%", render: t => date(t.at) },
            { id: "branch", label: "Branch", width: "12%", render: t => <span style={{ wordBreak: "break-all" }}>{t.titleBranch}</span> },
            { id: "side", label: "Side", width: "8%", render: t => <Chip size="small" color={t.side === "sold" ? "warning" : "info"} label={t.side} /> },
            { id: "goods", label: "Goods", width: "11%", render: t => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{t.goods}</span> },
            { id: "count", label: "Count", width: "8%", align: "right", render: t => num(t.count) },
            { id: "unitPrice", label: "Unit price", width: "10%", align: "right", render: t => num(t.unitPrice) },
            { id: "total", label: "Total", width: "10%", align: "right", render: t => num(t.count * t.unitPrice) },
            { id: "fee", label: "Fee (seller)", width: "9%", align: "right", render: t => num(t.fee) },
            { id: "other", label: "Other side", width: "17%", render: t => <MarketLink id={t.other} /> },
        ];
        const blocked = view?.blocked;
        const offers = view?.offers ?? [];
        return (
            <PageContainer>
                <SectionHeader title="Market player">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · one account's offers, holding, block and trades (every branch)</Typography>
                </SectionHeader>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField label="Player (link or id)" size="small" value={player} onChange={e => this.setState({ player: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.lookup(); }} sx={{ width: 300 }} />
                        <Button variant="outlined" onClick={this.lookup} disabled={busy || !player.trim()}>Look up</Button>
                        {view && (
                            <Typography variant="body2" component="div">
                                <b>{view.name}</b> · <PlayerLink id={view.account} />
                                {" "}<Chip size="small" color={blocked ? "error" : "success"} label={blocked ? "blocked by an operator" : "no operator block"} sx={{ ml: 0.5 }} />
                            </Typography>
                        )}
                    </Box>
                    {err("view")}
                    {notice && <Alert severity={notice.severity} sx={{ mt: 1 }} onClose={() => this.setState({ notice: null })}>{notice.text}</Alert>}
                </Paper>
                {view && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Reserved in market (the holding)</Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                    {(view.held ?? []).map(h => <Chip key={h.goods} size="small" variant="outlined" label={`${h.goods} × ${num(h.count)}`} />)}
                                    <Chip size="small" variant="outlined" label={`currency ${num(view.heldCurrency)}`} />
                                    <Typography variant="caption" color="text.secondary">{view.ops} step id(s) kept on the row</Typography>
                                </Box>
                            </Box>
                            {edit && (blocked
                                ? <Button variant="outlined" onClick={this.unblock} disabled={busy}>Unblock</Button>
                                : <Button variant="contained" color="error" onClick={this.block} disabled={busy}>Block…</Button>)}
                        </Box>
                        {blocked && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                Blocked by {blocked.by || "?"} on {date(blocked.at)}{blocked.reason ? ` - ${blocked.reason}` : " (no reason given)"}. Every market action answers 60.
                            </Alert>
                        )}
                        {!blocked && <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>No operator block; the title's clean-account rule (ban, migration verdict, a flagged match) is checked on every action and by the sweep.</Typography>}
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Offers ({offers.length}, every state)</Typography>
                        {offers.length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={offerColumns} rows={offers} getRowKey={o => o.id} />
                            : <Typography variant="body2" color="text.secondary">No offer.</Typography>}
                    </Paper>
                )}
                {(trades || errors.trades) && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Trades ({(trades?.trades ?? []).length}, newest first, at most 100)</Typography>
                        {trades && ((trades.trades ?? []).length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={tradeColumns} rows={trades.trades} getRowKey={t => `${t.id}-${t.side}`} />
                            : <Typography variant="body2" color="text.secondary">No trade.</Typography>)}
                        {err("trades")}
                    </Paper>
                )}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
